import { Card, CardHeader } from '@/components/ui';

import { count } from './format';

const TOP_UNIVERSITIES = 3;
const TOP_SKILLS = 6;
const TOP_YEARS = 4;

/**
 * Who applied, in one card: the top few universities, skills and graduation
 * years. The full ranked lists live behind `/companies/me/analytics/candidates`;
 * this is the brief version, so each list is cut to its first few rows.
 */
export function CandidateSnapshot({ insights, period, className }) {
  const applicants = insights?.applicants ?? 0;
  const universities = (insights?.universities ?? []).slice(0, TOP_UNIVERSITIES);
  const skills = (insights?.skills ?? []).slice(0, TOP_SKILLS);
  const years = (insights?.graduation_years ?? []).slice(0, TOP_YEARS);

  return (
    <Card className={className}>
      <CardHeader
        title="Candidate analytics"
        description={`Who applied in the last ${period} days.`}
        action={
          <span className="text-ink text-sm font-semibold tabular-nums">
            {count(applicants)}{' '}
            <span className="text-ink-muted font-normal">
              {applicants === 1 ? 'applicant' : 'applicants'}
            </span>
          </span>
        }
      />

      {applicants === 0 ? (
        <p className="text-ink-muted text-sm">
          Nobody has applied in this period yet. It fills in as applications arrive.
        </p>
      ) : (
        <div className="grid gap-6 md:grid-cols-3">
          <Section title="Top universities">
            {universities.length === 0 ? (
              <Empty>No universities recorded</Empty>
            ) : (
              <ol className="space-y-2">
                {universities.map((item, index) => (
                  <li key={item.label} className="flex items-baseline gap-2 text-sm">
                    <span className="text-ink-muted w-4 shrink-0 text-xs tabular-nums">
                      {index + 1}.
                    </span>
                    <span className="text-ink min-w-0 flex-1 truncate" title={item.label}>
                      {item.label}
                    </span>
                    <span className="text-ink-muted shrink-0 text-xs tabular-nums">
                      {count(item.count)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          <Section title="Top skills">
            {skills.length === 0 ? <Empty>No skills recorded</Empty> : <Chips items={skills} />}
          </Section>

          <Section title="Graduating">
            {years.length === 0 ? (
              <Empty>No graduation years recorded</Empty>
            ) : (
              <Chips items={years} />
            )}
          </Section>
        </div>
      )}
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <section className="min-w-0">
      <h3 className="text-ink-muted mb-2.5 text-[11px] font-medium tracking-wide uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Chips({ items }) {
  return (
    <ul className="flex flex-wrap gap-2">
      {items.map((item) => (
        <li
          key={item.label}
          className="bg-brand-50 text-brand-800 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
        >
          {item.label}
          <span className="text-brand-600 tabular-nums">{count(item.count)}</span>
        </li>
      ))}
    </ul>
  );
}

function Empty({ children }) {
  return <p className="text-ink-muted text-sm">{children}</p>;
}
